<template>
    <div class="@container flex flex-wrap w-full items-center gap-2 truncate">
        <date-range-selector v-model="dateRange"/>
        <div v-if="enableDeletedTransactionFilter" class="flex ml-4">
            <USwitch
                v-model="showDeleted"
                label="Vis slettede bilag"
                class="mx-2"
                size="lg"
                autocomplete="on"
                @click="toggleIsDeleted"
            />
        </div>

        <transaction-filter-account
            v-if="enableAccountFilter"
            :account-no="accountNo"
            @on-account-no-selected="onAccountNoSelected"
        />

        <UInput
            v-if="enableSearchFilter"
            v-model="searchText"
            icon="i-ph-magnifying-glass"
            data-cy="account-filter"
            placeholder="Søk i bilag"
            class="grow max-w-[300px] truncate"
            autocomplete="off"
            @keyup="debouncedEmit"
        >
            <template #trailing>
                <UButton
                    v-if="searchText"
                    icon="i-ph-x"
                    size="sm"
                    color="neutral"
                    variant="ghost"
                    @click="clearSearch"
                />
                <div v-else/>
            </template>
        </UInput>
        <department-picker-new
            v-model="department"
            size="md"
        />
        <project-picker-new
            v-model="project"
            size="md"
        />
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {debounce} from 'lodash'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {format} from 'date-fns'
import {getPersistedRangeOr} from '@/dateUtils/dateRange.service'
import TransactionFilterAccount from '@/transaction/TransactionFilterAccount.vue'
import DepartmentPickerNew from '@/dimensions/DepartmentPickerNew.vue'
import ProjectPickerNew from '@/dimensions/ProjectPickerNew.vue'

const props = defineProps({
    enableAccountFilter: {type: Boolean, default: false},
    enableSearchFilter: {type: Boolean, default: false},
    enableDateFilter: {type: Boolean, default: false},
    enableDeletedTransactionFilter: {type: Boolean, default: false},
    accountNo: {type: String, default: undefined},
})

const searchParams = ref<TransactionSearchParams>(defaultSearchParams(props.enableDeletedTransactionFilter ? false : undefined))
const searchText = ref('')
const showDeleted = ref(searchParams.value.isDeleted)

const dateRange = computed({
    get: () => {
        return getPersistedRangeOr({
            start: new Date(searchParams.value.fromDate!),
            end: new Date(searchParams.value.toDate!)
        })
    },
    set: (dateRange) => {
        searchParams.value.fromDate = format(dateRange.start, 'yyyy-MM-dd')
        searchParams.value.toDate = format(dateRange.end, 'yyyy-MM-dd')
        emitChange()
    }
})

const toggleIsDeleted = () => {
    showDeleted.value = !showDeleted.value
    if (showDeleted.value) {
        searchParams.value.isDeleted = undefined
    } else {
        searchParams.value.isDeleted = false
    }
    emitChange()
}

const onAccountNoSelected = (accountNo: string) => {
    searchParams.value.accountNo = accountNo
    emitChange()
}

const department = computed<string>({
    get: () => {
        return searchParams.value.department || ''
    },
    set: (value: string) => {
        searchParams.value.department = value || undefined
        emitChange()
    }
})

const project = computed<string>({
    get: () => {
        return searchParams.value.project || ''
    },
    set: (value: string) => {
        searchParams.value.project = value || undefined
        emitChange()
    }
})

const clearSearch = () => {
    searchText.value = ''
    debouncedEmit()
}

const emit = defineEmits(['update'])

const emitChange = () => {
    if (searchText.value === '') {
        searchParams.value.query = undefined
    } else {
        searchParams.value.query = searchText.value
    }
    emit('update', searchParams.value)
}
const debouncedEmit = debounce(emitChange, 500)

// @ts-ignore
if (window.Cypress) {
// @ts-ignore
    window.quickSetFilter = (fromDate: string, toDate: string) => {
        searchParams.value.fromDate = fromDate
        searchParams.value.toDate = toDate
        emitChange()
    }
}

onMounted(emitChange)

</script>

<style>

:where(.ag-header-cell:not(.ag-floating-filter) *, .ag-header-group-cell *) {
    position: relative;
    z-index: 0;
}

</style>